import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { FormErrorMessage, FormikSubmitButton, TextInput } from "../components/fieldset.tsx";
import { AuthPageHeader } from "../components/frame.tsx";
import { Link } from "../components/link.tsx";
import { SessionContext } from "../session-context.ts";
import { Page, User } from "../types.ts";
import { usePageTitle } from "../utils.ts";
import { SigninFormValues, submitSigninForm } from "./signin.lib.ts";

const initialValues: SigninFormValues = { email: "", password: "" };

const SigninSchema = toFormikValidationSchema(
  z
    .object({
      email: z
        .string({ required_error: "Email is required" })
        .email("A valid email is required")
        .min(1, "Email is required"),
      password: z.string({ required_error: "Password is required" }).min(1, "Password is required"),
    })
    .required({ email: true, password: true }),
);

function SigninForm(props: FormikProps<SigninFormValues>) {
  return (
    <Form className="space-y-6">
      {props.status && (
        <FormErrorMessage>
          {props.status.cause || "An error occured during signin, try again"}
        </FormErrorMessage>
      )}

      <Field name="email">
        {({ field, meta }: FieldProps) => (
          <TextInput
            label="Email"
            inputProps={{ ...field, type: "email", autoComplete: "email" }}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>

      <Field name="password">
        {({ field, meta }: FieldProps) => (
          <TextInput
            label="Password"
            inputProps={{ ...field, type: "password", autoComplete: "new-password" }}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>

      <div className="flex justify-end">
        <p className="mt-2 text-sm leading-6 text-zinc-500">
          <Link to={Page.ForgotPassword}>Forgot Password?</Link>
        </p>
      </div>

      <FormikSubmitButton
        className="w-full"
        title="Sign in"
        isSubmitting={props.isSubmitting}
      />
    </Form>
  );
}

export function SigninPage() {
  usePageTitle("Sign in");
  const session = useContext(SessionContext);

  if (session.session.user) {
    return <Navigate to={Page.Plan} />;
  }

  return (
    <>
      <AuthPageHeader
        heading="Sign in to MealsMatter"
        extra=<p className="mt-2 text-sm leading-6 text-zinc-500">
          Not a member? <Link to={Page.Signup}>Start a 14 day free trial</Link>
        </p>
      />

      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={SigninSchema}
          onSubmit={async (values: SigninFormValues, actions: FormikHelpers<SigninFormValues>) => {
            const user: User | undefined = await submitSigninForm(values, actions);

            session.setSession((s) => {
              return { ...s, user: user };
            });
          }}
        >
          {/*Note this is a function not a component*/}
          {SigninForm}
        </Formik>
      </div>
    </>
  );
}
